import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';

const CustomTextField = ({ ...props }) => (
  <TextField
    fullWidth
    variant="outlined"
    {...props}
    sx={{
      '& .MuiOutlinedInput-root': {
        color: '#F5F7FA',
        bgcolor: 'rgba(255, 255, 255, 0.03)',
        '& fieldset': { borderColor: '#334155' },
        '&:hover fieldset': { borderColor: '#475569' },
        '&.Mui-focused fieldset': { borderColor: '#3B82F6' }
      },
      '& .MuiInputLabel-root': { color: '#94A3B8' },
      '& .MuiInputLabel-root.Mui-focused': { color: '#3B82F6' },
      ...props.sx
    }}
  />
);

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { resetPassword } = useAuth();

  const handleClose = () => setSnackbar(prev => ({ ...prev, open: false }));

  async function handleSubmit(e) {
    e.preventDefault();

    if (!email) {
      setSnackbar({ open: true, message: 'Please enter your email address', severity: 'error' });
      return;
    }

    try {
      setLoading(true);
      await resetPassword(email);
      setSnackbar({
        open: true,
        message: 'Password reset link sent! Check your inbox (including spam) for the link from admin@verveai.co.',
        severity: 'success'
      });
      setEmail('');
    } catch (err) {
      console.error('Password reset error:', err);
      setSnackbar({ open: true, message: 'Failed to reset password. Please try again.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0A0E17', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            bgcolor: '#0F141F',
            border: '1px solid #1E293B',
            borderRadius: 2
          }}
        >
          <Box textAlign="center" mb={4}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 2,
                borderRadius: '50%',
                bgcolor: 'rgba(59, 130, 246, 0.1)',
                mb: 2
              }}
            >
              <EmailIcon sx={{ fontSize: 32, color: '#3B82F6' }} />
            </Box>
            <Typography component="h1" variant="h5" fontWeight="700" color="#F5F7FA" gutterBottom>
              Reset Password
            </Typography>
            <Typography variant="body2" color="#B0B3C0">
              Enter the email associated with your account and we'll send you a link to reset your password.
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <CustomTextField
              margin="normal"
              required
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: '#64748B' }} />
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #007AFF 0%, #00E676 100%)',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none'
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
            </Button>
          </form>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Typography color="#3B82F6" variant="body2" sx={{ '&:hover': { color: '#60A5FA' } }}>
                &larr; Back to Sign In
              </Typography>
            </Link>
          </Box>
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleClose}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
